// 🔧 event_builder.js
// ✅ Purpose: Event document builder for the LGV Demo Engine
// ✅ Features:
// - Creates the main Firestore event document for demo events
// - Generates industry-specific event names
// - Generates safe unique event slugs
// - Uses Demo Engine event defaults
// - Returns eventId, slug, eventRef, and eventDoc for downstream builders
// - Keeps event creation separate from categories, entries, users, and votes
// - Fully block-commented for clarity and future devs

import {
  collection,
  doc,
  getDocs,
  limit,
  query,
  serverTimestamp,
  where,
  writeBatch,
} from "firebase/firestore";
import { db } from "../../firebase";
import { logger } from "../../utils/logger";
import {
  generateDemoEventName,
  generateDemoEventSlug,
} from "../generators/event_name_generator";
import { buildDemoEventDoc } from "../templates/event_defaults";
import {
  adaptExperienceManifest,
  validateExperienceMetadata,
} from "../core/experience_metadata_adapter";

export const buildEvent = async ({ user, demoConfig, industryTemplate } = {}) => {
  const eventName = generateDemoEventName({
    industryKey: industryTemplate?.key || demoConfig?.industry || "car_show",
    industryTemplate,
    eventTitle: demoConfig?.eventTitle,
  });

  const isLiveExample = demoConfig?.intent === "live_example";
  const slug = generateDemoEventSlug({
    eventName,
    includeUniqueSuffix: !isLiveExample,
  });

  if (isLiveExample) {
    const existingSlug = await getDocs(
      query(collection(db, "events"), where("slug", "==", slug), limit(1))
    );

    if (!existingSlug.empty) {
      throw new Error(
        `A site already uses /${slug}. Choose a different event title.`
      );
    }
  }

  const experienceMetadata =
    adaptExperienceManifest(
      industryTemplate
    );


  const metadataValidation =
    validateExperienceMetadata(
      experienceMetadata
    );


  if (
    !metadataValidation.valid
  ) {

    throw new Error(
      "Experience metadata validation failed."
    );

  }

  const eventDoc = buildDemoEventDoc({
    user,
    demoConfig,
    industryTemplate,
    eventName,
    slug,
    experienceMetadata,
  });

  logger.log("🧱 Event Builder creating Firestore event:", {
    value: {
      eventName,
      slug,
      industry: industryTemplate?.key || demoConfig?.industry || "car_show",
    },
  });

  const eventRef = doc(collection(db, "events"));

  // =====================================================
  // ✅ Bootstrap the authenticated Experience creator
  // =====================================================
  // Firestore already recognizes event.ownerId as an event
  // administrator for client-side event configuration writes.
  //
  // The Integrity Engine's validateActor() intentionally uses
  // global/event role documents rather than ownerId, however.
  //
  // Creating this canonical event-scoped manager document lets
  // the same authenticated creator use trusted Cloud Functions
  // such as updateVotingSettings without weakening either the
  // Firestore Rules or the Integrity Engine authorization model.
  // =====================================================

  const creatorMembershipRef =
    doc(
      db,
      "events",
      eventRef.id,
      "users",
      user.uid
    );

  const batch = writeBatch(db);

  batch.set(eventRef, eventDoc);
  batch.set(
    creatorMembershipRef,
    {
      uid: user.uid,

      email:
        user.email || "",

      displayName:
        user.displayName || "",

      role: "manager",

      permissions: {},

      pendingManager: false,

      status: "active",

      demoSeed: true,

      demoCreator: true,

      assignedAt:
        serverTimestamp(),

      updatedAt:
        serverTimestamp(),

      updatedBy:
        user.uid,
    },
    { merge: true }
  );

  await batch.commit();

  logger.log("✅ Event Builder created Experience creator manager membership:", {
    value: {
      eventId: eventRef.id,
      creatorUid: user.uid,
    },
  });

  logger.log("✅ Event Builder created Firestore event:", {
    value: {
      eventId: eventRef.id,
      eventName,
      slug,
    },
  });

  return {
    builder: "event_builder",
    ready: true,
    eventId: eventRef.id,
    eventRef,
    eventDoc,
    eventName,
    slug,
    message: "Event Builder created the main demo event document.",
  };
};

// =====================================================
// 🧱 Compatibility Shell
// =====================================================
// Purpose:
// - Keeps existing pipeline shell imports working during migration
// - Now delegates to real Event Builder behavior when user is available
// =====================================================
export const buildEventShell = async ({ user, demoConfig, industryTemplate } = {}) => {
  if (!user?.uid) {
    logger.warn("⚠️ Event Builder shell used without authenticated user.", {
      value: {
        industry: industryTemplate?.key || demoConfig?.industry || "car_show",
      },
    });

    return {
      builder: "event_builder",
      ready: false,
      message: "Event Builder needs an authenticated user before Firestore creation.",
    };
  }

  return buildEvent({
    user,
    demoConfig,
    industryTemplate,
  });
};
